import { InputGroup, Intent } from '@blueprintjs/core';
import { Button, SimplifiedTooltip } from '@moleculeone/ui';
import { Target } from '@retro-ui/api-contract';
import { css } from '@stitches/react';
import { CellContext } from '@tanstack/react-table';
import { ChangeEvent, ClipboardEvent } from 'react';

import { MoleculePreview } from './MoleculePreview';

const smilesInputStyle = css({
  fontFamily: 'monospace',
});

interface InputProps {
  cellContext: CellContext<Target, string>;
  onChange: (columnId: string, rowIndex: number, value: string) => void;
}

interface SmilesInputProps extends InputProps {
  onPaste: (rowIndex: number, value: string) => void;
  onDraw: (rowIndex: number) => void;
}

export function SmilesInput(props: SmilesInputProps) {
  const { cellContext: ctx, onChange, onPaste, onDraw } = props;
  const smilesErrors = ctx.table.options.meta?.smilesErrors ?? [];

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(ctx.column.id, ctx.row.index, event.target.value);
  };

  const onInputPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    onPaste(ctx.row.index, event.clipboardData.getData('text'));
  };

  const drawSmiles = () => {
    onDraw(ctx.row.index);
  };

  const smilesError = smilesErrors.find(smilesError => smilesError.smiles === ctx.getValue());
  const smilesErrorMessage = smilesError?.message;

  return (
    <InputGroup
      inputClassName={smilesInputStyle()}
      data-cy="smiles-input"
      large
      placeholder="Enter SMILES..."
      defaultValue={ctx.getValue()}
      onBlur={onInputChange}
      onPaste={onInputPaste}
      intent={smilesError ? Intent.WARNING : Intent.NONE}
      rightElement={
        smilesError ? (
          <SimplifiedTooltip content={smilesErrorMessage} contentProps={{ side: 'bottom' }}>
            <Button weight="minimal" icon="warning" theme={smilesError ? 'amber' : 'slate'} />
          </SimplifiedTooltip>
        ) : (
          <SimplifiedTooltip content="Open in compound editor" contentProps={{ side: 'bottom' }}>
            <Button data-cy="edit-target-button" weight="minimal" icon="edit" onClick={drawSmiles} />
          </SimplifiedTooltip>
        )
      }
      leftElement={<MoleculePreview smiles={ctx.getValue()} disabled={Boolean(smilesError)} />}
    />
  );
}
